import { Directive, ElementRef, HostListener, Input, OnDestroy, Renderer2, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Country } from './countries.model';

@Directive({
  selector: 'input[appCountrySelect]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CountrySelectDirective),
      multi: true,
    },
  ],
})
export class CountrySelectDirective implements ControlValueAccessor, OnDestroy {
  private static idCounter = 0;
  private readonly panelId = `country-select-panel-${CountrySelectDirective.idCounter++}`;

  @Input('appCountrySelect')
  set countriesInput(value: Country[] | null) {
    this.countries = value ?? [];
    if (this.isOpen) this.renderOptions();
  }

  private countries: Country[] = [];
  private selected: Country | null = null;
  private query = '';
  private isOpen = false;
  private highlightIndex = -1;
  private filtered: Country[] = [];
  private disabled = false;

  private panelEl: HTMLElement | null = null;
  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  private unlistenDocClick?: () => void;
  private unlistenResize?: () => void;

  private flagIconEl: HTMLImageElement | null = null;
  private originalPaddingLeft: string | null = null;
  private unlistenFlagResize?: () => void;

  constructor(
    private el: ElementRef<HTMLInputElement>,
    private renderer: Renderer2,
  ) {
    renderer.setAttribute(el.nativeElement, 'autocomplete', 'off');
    renderer.setAttribute(el.nativeElement, 'role', 'combobox');
    renderer.setAttribute(el.nativeElement, 'aria-expanded', 'false');
    renderer.setAttribute(el.nativeElement, 'aria-autocomplete', 'list');
    renderer.setAttribute(el.nativeElement, 'aria-haspopup', 'listbox');
    this.unlistenFlagResize = renderer.listen('window', 'resize', () => this.repositionFlagIcon());
  }

  writeValue(code: string): void {
    const normalized = String(code ?? '').trim().toUpperCase();
    this.selected = this.countries.find((c) => c.code.toUpperCase() === normalized) ?? null;
    this.applyDisplayValue();
    this.updateFlagIcon();
  }

  registerOnChange(fn: (value: string) => void): void { this.onChange = fn; }
  registerOnTouched(fn: () => void): void { this.onTouched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.renderer.setProperty(this.el.nativeElement, 'disabled', isDisabled);
  }

  @HostListener('focus')
  onFocus(): void { if (!this.disabled) this.openForEditing(); }

  @HostListener('click')
  onClick(): void { if (!this.disabled) this.openForEditing(); }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (this.disabled) return;
    const value = (event.target as HTMLInputElement).value;
    this.query = value;
    if (!this.isOpen) this.openPanel();
    else this.renderOptions();
  }

  @HostListener('blur')
  onBlur(): void { this.closePanel(true); }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (this.disabled) return;
    if (!this.isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter') {
        event.preventDefault();
        this.openForEditing();
      }
      return;
    }
    switch (event.key) {
      case 'ArrowDown': event.preventDefault(); this.moveHighlight(1); break;
      case 'ArrowUp': event.preventDefault(); this.moveHighlight(-1); break;
      case 'Enter':
        event.preventDefault();
        if (this.highlightIndex >= 0 && this.filtered[this.highlightIndex]) {
          this.selectCountry(this.filtered[this.highlightIndex]);
        }
        break;
      case 'Escape': event.preventDefault(); this.closePanel(true); break;
      case 'Tab': this.closePanel(true); break;
    }
  }

  ngOnDestroy(): void {
    this.closePanel(false);
    this.unlistenFlagResize?.();
    if (this.flagIconEl) { this.flagIconEl.remove(); this.flagIconEl = null; }
  }

  private openPanel(): void {
    if (this.isOpen || this.disabled || this.countries.length === 0) return;
    this.isOpen = true;
    this.highlightIndex = -1;
    this.renderer.setAttribute(this.el.nativeElement, 'aria-expanded', 'true');
    this.buildPanel();
    this.renderOptions();
    this.attachGlobalListeners();
  }

  private openForEditing(): void {
    this.query = '';
    this.renderer.setProperty(this.el.nativeElement, 'value', '');
    this.openPanel();
  }

  private closePanel(revertDisplay: boolean): void {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.renderer.setAttribute(this.el.nativeElement, 'aria-expanded', 'false');
    if (this.panelEl) {
      const parent = this.el.nativeElement.parentElement;
      if (parent) this.renderer.removeChild(parent, this.panelEl);
      this.panelEl = null;
    }
    this.renderer.removeAttribute(this.el.nativeElement, 'aria-controls');
    this.renderer.removeAttribute(this.el.nativeElement, 'aria-activedescendant');
    this.detachGlobalListeners();
    if (revertDisplay) {
      this.query = '';
      this.applyDisplayValue();
      this.onTouched();
    }
  }

  private applyDisplayValue(): void {
    const text = this.selected ? this.selected.name : '';
    this.renderer.setProperty(this.el.nativeElement, 'value', text);
  }

  private flagUrl(country: Country): string {
    return `/assets/flags/${country.code.toLowerCase()}.svg`;
  }

  private normalize(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
  }

  // Panel INLINE (position:absolute dentro del mismo contenedor del input),
  // no se porta a document.body ni a ningún overlay.
  // Banderas: SVG de flag-icons en /assets/flags; si falta el archivo, se oculta el <img>.
  private ensureRelativeParent(): HTMLElement | null {
    const parent = this.el.nativeElement.parentElement;
    if (!parent) return null;
    if (getComputedStyle(parent).position === 'static') {
      this.renderer.setStyle(parent, 'position', 'relative');
    }
    return parent;
  }

  private buildPanel(): void {
    const parent = this.ensureRelativeParent();
    if (!parent) return;
    const panel = this.renderer.createElement('div') as HTMLElement;
    this.renderer.setAttribute(panel, 'role', 'listbox');
    this.renderer.setAttribute(panel, 'id', this.panelId);
    this.renderer.setAttribute(this.el.nativeElement, 'aria-controls', this.panelId);
    Object.assign(panel.style, {
      position: 'absolute',
      zIndex: '1',
      background: '#ffffff',
      border: '1px solid #d0d5dd',
      borderRadius: '10px',
      boxShadow: '0 8px 24px rgba(16, 24, 40, 0.12)',
      maxHeight: '260px',
      overflowY: 'auto',
      padding: '4px',
      boxSizing: 'border-box',
      font: 'inherit',
      fontSize: '14px',
    });
    this.renderer.appendChild(parent, panel);
    this.panelEl = panel;
    this.reposition();
  }

  private reposition(): void {
    if (!this.panelEl) return;
    const input = this.el.nativeElement;
    Object.assign(this.panelEl.style, {
      top: `${input.offsetTop + input.offsetHeight + 4}px`,
      left: `${input.offsetLeft}px`,
      width: `${input.offsetWidth}px`,
    });
  }

  private renderOptions(): void {
    if (!this.panelEl) return;
    const normalizedQuery = this.normalize(this.query);
    this.filtered = normalizedQuery
      ? this.countries.filter((c) => this.normalize(c.name).includes(normalizedQuery))
      : this.countries;

    this.panelEl.innerHTML = '';

    if (this.filtered.length === 0) {
      const empty = this.renderer.createElement('div');
      Object.assign(empty.style, { padding: '10px 12px', color: '#98a2b3' });
      this.renderer.appendChild(empty, this.renderer.createText('Sin resultados'));
      this.renderer.appendChild(this.panelEl, empty);
      return;
    }

    this.filtered.forEach((country, index) => {
      const option = this.renderer.createElement('div') as HTMLElement;
      this.renderer.setAttribute(option, 'role', 'option');
      this.renderer.setAttribute(option, 'id', `${this.panelId}-option-${index}`);
      this.renderer.setAttribute(option, 'aria-selected', 'false');
      Object.assign(option.style, {
        display: 'flex', alignItems: 'center', gap: '8px', padding: '9px 12px',
        borderRadius: '6px', cursor: 'pointer', whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis',
      });

      const flag = this.renderer.createElement('img') as HTMLImageElement;
      this.renderer.setAttribute(flag, 'src', this.flagUrl(country));
      this.renderer.setAttribute(flag, 'alt', '');
      this.renderer.setAttribute(flag, 'loading', 'lazy');
      Object.assign(flag.style, { width: '20px', height: '15px', objectFit: 'cover', borderRadius: '2px', flexShrink: '0' });
      this.renderer.listen(flag, 'error', () => { flag.style.visibility = 'hidden'; });
      this.renderer.appendChild(option, flag);

      const label = this.renderer.createElement('span');
      Object.assign((label as HTMLElement).style, { overflow: 'hidden', textOverflow: 'ellipsis' });
      this.renderer.appendChild(label, this.renderer.createText(country.name));
      this.renderer.appendChild(option, label);

      this.renderer.listen(option, 'mousedown', (event: Event) => {
        event.preventDefault();
        this.selectCountry(country);
      });
      this.renderer.listen(option, 'mouseenter', () => this.setHighlight(index));
      this.renderer.appendChild(this.panelEl!, option);
    });

    this.setHighlight(this.filtered.length > 0 ? 0 : -1);
    this.reposition();
  }

  private setHighlight(index: number): void {
    if (!this.panelEl) return;
    this.highlightIndex = index;
    let activeDescendantId: string | null = null;
    Array.from(this.panelEl.children).forEach((child, i) => {
      const isActive = i === index;
      (child as HTMLElement).style.background = isActive ? '#f2f4f7' : 'transparent';
      child.setAttribute('aria-selected', isActive ? 'true' : 'false');
      if (isActive) activeDescendantId = child.id;
    });
    if (activeDescendantId) {
      this.renderer.setAttribute(this.el.nativeElement, 'aria-activedescendant', activeDescendantId);
    } else {
      this.renderer.removeAttribute(this.el.nativeElement, 'aria-activedescendant');
    }
  }

  private moveHighlight(delta: number): void {
    if (this.filtered.length === 0) return;
    const next = (this.highlightIndex + delta + this.filtered.length) % this.filtered.length;
    this.setHighlight(next);
    this.panelEl?.children[next]?.scrollIntoView({ block: 'nearest' });
  }

  private selectCountry(country: Country): void {
    this.selected = country;
    this.onChange(country.code.toUpperCase());
    this.closePanel(true);
    this.updateFlagIcon();
  }

  private updateFlagIcon(): void {
    if (this.selected) this.showFlagIcon(this.selected);
    else this.hideFlagIcon();
  }

  private ensureFlagIcon(): HTMLImageElement | null {
    if (this.flagIconEl) return this.flagIconEl;
    const parent = this.ensureRelativeParent();
    if (!parent) return null;
    const icon = this.renderer.createElement('img') as HTMLImageElement;
    this.renderer.setAttribute(icon, 'alt', '');
    this.renderer.setAttribute(icon, 'aria-hidden', 'true');
    Object.assign(icon.style, {
      position: 'absolute', width: '20px', height: '15px', objectFit: 'cover',
      borderRadius: '2px', pointerEvents: 'none', zIndex: '1',
    });
    this.renderer.listen(icon, 'error', () => { icon.style.visibility = 'hidden'; });
    this.renderer.appendChild(parent, icon);
    this.flagIconEl = icon;
    return icon;
  }

  // Este método solo es necesario si el input tiene `transition` en su CSS.
  // El input de país en este proyecto no la tiene hoy (ver checkout.component.scss),
  // pero se deja el fix igual por robustez ante cambios futuros de estilo.
  private captureOriginalPadding(): void {
    const el = this.el.nativeElement;
    const ownOverridePadding = el.style.paddingLeft;
    const ownOverrideTransition = el.style.transition;
    el.style.transition = 'none';
    el.style.paddingLeft = '';
    void el.offsetWidth; // fuerza reflow síncrono
    this.originalPaddingLeft = getComputedStyle(el).paddingLeft || '0px';
    el.style.paddingLeft = ownOverridePadding;
    el.style.transition = ownOverrideTransition;
  }

  private showFlagIcon(country: Country): void {
    this.captureOriginalPadding();
    const icon = this.ensureFlagIcon();
    if (!icon) return;
    icon.src = this.flagUrl(country);
    icon.style.visibility = 'visible';
    icon.style.display = 'block';
    this.renderer.setStyle(this.el.nativeElement, 'padding-left', `calc(${this.originalPaddingLeft} + 28px)`);
    this.repositionFlagIcon();
  }

  private hideFlagIcon(): void {
    if (this.flagIconEl) this.flagIconEl.style.display = 'none';
    if (this.originalPaddingLeft !== null) {
      this.renderer.setStyle(this.el.nativeElement, 'padding-left', this.originalPaddingLeft);
    }
  }

  private repositionFlagIcon(): void {
    if (!this.flagIconEl || this.flagIconEl.style.display === 'none') return;
    const input = this.el.nativeElement;
    this.captureOriginalPadding();
    const inset = parseFloat(this.originalPaddingLeft || '0');
    this.flagIconEl.style.left = `${input.offsetLeft + inset}px`;
    this.flagIconEl.style.top = `${input.offsetTop + (input.offsetHeight - 15) / 2}px`;
    this.renderer.setStyle(input, 'padding-left', `calc(${this.originalPaddingLeft} + 28px)`);
  }

  private attachGlobalListeners(): void {
    this.unlistenDocClick = this.renderer.listen('document', 'mousedown', (event: MouseEvent) => {
      const target = event.target as Node;
      if (this.el.nativeElement.contains(target) || this.panelEl?.contains(target)) return;
      this.closePanel(true);
    });
    this.unlistenResize = this.renderer.listen('window', 'resize', () => this.reposition());
  }

  private detachGlobalListeners(): void {
    this.unlistenDocClick?.();
    this.unlistenResize?.();
    this.unlistenDocClick = undefined;
    this.unlistenResize = undefined;
  }
}
